// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { TextField, TextFieldProps } from '@mui/material';
import { forwardRef } from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';

interface Props
  extends Pick<
    TextFieldProps,
    | 'fullWidth'
    | 'size'
    | 'onBlur'
    | 'variant'
    | 'inputRef'
    | 'InputProps'
    | 'InputLabelProps'
    | 'label'
    | 'placeholder'
    | 'disabled'
  > {
  value: string | number | null;
  name?: string;
  error?: boolean;
  helperText?: TextFieldProps['helperText'];

  /**
   * If true, the input will allow zero as a valid value.
   *
   * @default true
   */
  allowZero?: boolean;

  /**
   * If true, the input will only allow two decimal places of precision.
   *
   * @default false
   */
  isCurrency?: boolean;

  /**
   * If true, the input will display a $ prefix.
   *
   * @default false
   */
  showDollarPrefix?: boolean;

  /**
   * If true, the input will allow negative values.
   *
   * @default false
   */
  allowNegative?: boolean;

  /**
   * If provided, the maximum number of decimal places to allow. Providing a
   * value for this prop will override the behavior of the `isCurrency` prop.
   *
   * @default 0
   */
  maximumFractionDigits?: number;

  /**
   * A callback fired when the value changes.
   */
  onChange: (values: NumberFormatValues) => void;
}

// Returns the significant decimal places of a numeric field value.
function countFractionDigits(value: string | number | null): number {
  if (value === null) {
    return 0;
  }

  return parseCurrencyAmount(String(value))?.decimalPlaces() ?? 0;
}

export const NumberTextField = forwardRef<HTMLInputElement, Props>(
  (props, ref) => {
    const allowZero = props.allowZero ?? true;
    const isString = typeof props.value === 'string';
    const maximumFractionDigits =
      props.maximumFractionDigits ?? (props.isCurrency ? 2 : 0);

    // Convert null to empty string for NumericFormat
    const numericFormatValue = props.value === null ? '' : props.value;

    // A currency value finer than the allowed scale is shown unrounded, so the
    // user sees the digits its validation error is about instead of a value
    // the library rounded for display.
    const isCurrencyValueBeyondScale =
      props.isCurrency === true &&
      countFractionDigits(props.value) > maximumFractionDigits;
    const decimalScale = isCurrencyValueBeyondScale
      ? undefined
      : maximumFractionDigits;

    const isAllowed = (values: NumberFormatValues) => {
      if (values.floatValue === 0) {
        return allowZero;
      }

      return true;
    };

    // `react-number-format` fires `onValueChange` for BOTH user-driven
    // edits (source === 'event') and programmatic `value` prop changes
    // (source === 'prop'). Callers generally expect an onChange that
    // behaves like a normal controlled input — only firing on user
    // interaction — so we filter here. Without this, any parent that
    // drives `value` reactively (e.g. a slider updating a paired number
    // input) would feed back into its own onChange handler on every
    // render and loop straight back out again.
    const handleValueChange = (
      values: NumberFormatValues,
      sourceInfo: { source: string }
    ) => {
      if (sourceInfo.source !== 'event') {
        return;
      }
      props.onChange(values);
    };

    return (
      <NumericFormat
        thousandSeparator
        name={props.name}
        size={props.size}
        label={props.label}
        value={numericFormatValue}
        error={props.error}
        onBlur={props.onBlur}
        variant={props.variant}
        inputRef={ref}
        isAllowed={isAllowed}
        fullWidth={props.fullWidth}
        helperText={props.helperText}
        placeholder={props.placeholder}
        decimalScale={decimalScale}
        onValueChange={handleValueChange}
        allowNegative={props.allowNegative}
        fixedDecimalScale={props.isCurrency && !isCurrencyValueBeyondScale}
        valueIsNumericString={isString}
        prefix={props.showDollarPrefix ? '$' : undefined}
        disabled={props.disabled}
        customInput={TextField}
        InputProps={{
          inputProps: { style: { textAlign: 'right' } },
          ...props.InputProps,
        }}
        InputLabelProps={props.InputLabelProps}
      />
    );
  }
);
